import { useLayoutEffect, useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import { isBefore, addMinutes } from 'date-fns';
import { loadScript } from 'lib/script';
import { MASTERCARD_HOST, MASTERCARD_VERSION, MASTERCARD_MERCHANT_ID } from 'config';
import theme from 'lib/theme';
import resolveErrorMessage from './resolveErrorMessage';
import useFieldReducer, { FIELD_ACTIONS, INVALID_FIELD_ERROR_KEY, REQUIRED_FIELD_ERROR_KEY } from './useFieldReducer';
import { useInterval } from 'react-use';

const SESSION_TIMEOUT_MINS = 20;
const MAX_SCRIPT_LOAD_RETRIES = 10;
const createFormKey = () => new Date().getTime();

const useMastercardSession = ({ cardFields, updateCreditCard }) => {
  const isMounted = useRef(true);
  const [hasInitialized, setInitialized] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isUpdatingMastercardSession, setUpdatingMastercardSession] = useState(false);
  const [hasLoadingError, setLoadingError] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState();
  const [formKey, setFormKey] = useState(createFormKey());
  const [fields, dispatchFieldAction] = useFieldReducer();
  const expiryMonth = get(fields, 'expiryMonth.value');
  const expiryYear = get(fields, 'expiryYear.value');

  useLayoutEffect(() => {
    !hasInitialized && loadAndInitializeMastercard();
  }, [hasInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      isMounted.current = false;
      window.PaymentSession = null;
    }; // cleanup when the host component is unmounted
  }, []);

  useEffect(() => {
    const { PaymentSession } = window;
    if (PaymentSession) updateMasterCardSession();

    const today = new Date();
    const expiryDate = new Date(Number(expiryYear), Number(expiryMonth));

    if (expiryMonth && expiryYear && isBefore(expiryDate, today)) {
      dispatchFieldAction({
        type: FIELD_ACTIONS.SET_FIELD_ERROR,
        payload: { name: 'expiryMonth', error: { message: resolveErrorMessage('expiryMonth', INVALID_FIELD_ERROR_KEY) } },
      });
    } else if (expiryMonth) {
      dispatchFieldAction({
        type: FIELD_ACTIONS.SET_FIELD_ERROR,
        payload: { name: 'expiryMonth', error: null },
      });
    }
  }, [expiryMonth, expiryYear, dispatchFieldAction]);

  useInterval(() => {
    if (isBefore(sessionExpiry, Date.now())) {
      reinitializeForm();
    }
  }, 10000);

  const updateMasterCardSession = () => {
    const { PaymentSession } = window;
    if (PaymentSession) {
      setUpdatingMastercardSession(true);
      PaymentSession.updateSessionFromForm('card');
    }
  };

  const updateMasterCardField = ({ fieldName }) => {
    updateMasterCardSession();
    dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_TOUCHED, payload: { name: fieldName } });
  };

  const loadAndInitializeMastercard = async () => {
    setInitialized(false);
    setLoading(true);
    setLoadingError(false);

    try {
      await loadScript({
        url: `${MASTERCARD_HOST}/form/version/${MASTERCARD_VERSION}/merchant/${MASTERCARD_MERCHANT_ID}/session.js`,
        maxRetries: MAX_SCRIPT_LOAD_RETRIES,
      });
      isMounted.current && initializeMastercard();
    } catch (e) {
      isMounted.current && setLoadingError(true);
    }
  };

  // To re-initialize the form we need to first remove the existing form and re-render it.
  // The formKey is exposed to the host component and should be used as a component key to a parent
  // component of the form fields. This wil ensure that any iframes already initialized are removed, the
  // form fields re-rendered, ready for reinitialization.  Setting initialized to false will force a reload and
  // initialization of the mastercard script.
  const reinitializeForm = () => {
    setFormKey(createFormKey());
    setInitialized(false);
  };

  const initializeMastercard = () => {
    setLoading(false);
    dispatchFieldAction({ type: FIELD_ACTIONS.RESET });

    updateCreditCard({
      nameOnCard: null,
      sessionId: null,
      expiryMonth: null,
      expiryYear: null,
      cardType: null,
      cardNumber: null,
      securityCode: null,
      apiVersion: null,
      merchantId: null,
    });

    setSessionExpiry(addMinutes(new Date(), SESSION_TIMEOUT_MINS));

    const { PaymentSession } = window;

    PaymentSession.configure({
      fields: {
        card: cardFields,
      },
      frameEmbeddingMitigation: ['javascript'],

      callbacks: {
        initialized: (response) => {
          if (!isMounted.current) return;

          if (response.status === 'system_error') {
            setLoadingError(true);
          } else {
            setInitialized(true);
          }
        },
        formSessionUpdate: (response) => {
          if (!isMounted.current) return;

          setUpdatingMastercardSession(false);

          switch (response.status) {
            case 'ok': {
              handleValidCreditCard(response);
              break;
            }
            case 'fields_in_error': {
              handleFieldsInError(response);
              break;
            }
            case 'system_error': {
              handleSystemError(response);
              break;
            }
            case 'request_timeout': {
              handleRequestTimeout();
              break;
            }
            // eslint-disable-next-line no-empty
            default: {
            }
          }
        },
      },
    });

    // MasterCard's onChange callback fires when the user leaves the field, instead of on each keystroke like a regular DOM onChange
    // using onChange fixes issues with e2e testing as blur event is not being triggered on Cypress
    PaymentSession.onChange(['card.number'], () => updateMasterCardField({ fieldName: 'cardNumber' }));
    PaymentSession.onChange(['card.securityCode'], () => updateMasterCardField({ fieldName: 'securityCode' }));

    // We also need onBlur callback to fire here to cover copy/paste issues
    PaymentSession.onBlur(['card.number'], () => updateMasterCardField({ fieldName: 'cardNumber' }));
    PaymentSession.onBlur(['card.securityCode'], () => updateMasterCardField({ fieldName: 'securityCode' }));

    PaymentSession.setFocusStyle(['card.number', 'card.securityCode'], {
      borderColor: theme.colors.brand.secondary,
    });

    const handleValidCreditCard = (response) => {
      const card = get(response, 'sourceOfFunds.provided.card');

      if (!card || !card.expiry) return; // we occasionally get ok resonses without card/expiry data in prod?

      // mastercard doesn't require a security code to create a valid session.
      // unfortunately, once the security code has been set, even if it removed, the
      // following continues to return a value of 'xxx'.
      if (card.securityCode) {
        ['cardNumber', 'securityCode'].forEach((field) => {
          dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_ERROR, payload: { name: field, error: null } });
        });
        updateCreditCardFromResponse(response);
      } else {
        dispatchFieldAction({
          type: FIELD_ACTIONS.SET_FIELD_ERROR,
          payload: { name: 'securityCode', error: { message: resolveErrorMessage('securityCode', REQUIRED_FIELD_ERROR_KEY) } },
        });
      }
    };

    const handleFieldsInError = (response) => {
      ['cardNumber', 'securityCode'].forEach((field) => {
        const errorCode = response.errors[field];
        if (errorCode) {
          dispatchFieldAction({
            type: FIELD_ACTIONS.SET_FIELD_ERROR,
            payload: { name: field, error: { message: resolveErrorMessage(field, errorCode) } },
          });
        } else {
          dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_ERROR, payload: { name: field, error: null } });
        }
      });
    };

    const handleSystemError = (response) => {
      //mastercard sessions can time out, requiring the form to be re-initialized
      if (/(expired|timed)/.test(String(response.errors.message).toLowerCase())) {
        reinitializeForm();
        // if a card number under 8 characters is entered in non-prod environments, a java exception is
        // returned as the error message.
      } else if (/internal server error/.test(String(response.errors.message).toLowerCase())) {
        dispatchFieldAction({
          type: FIELD_ACTIONS.SET_FIELD_ERROR,
          payload: { name: 'cardNumber', error: { message: resolveErrorMessage('cardNumber', INVALID_FIELD_ERROR_KEY) } },
        });
      }
    };

    const handleRequestTimeout = () => {
      updateMasterCardSession();
    };
  };

  const getFieldError = (fieldName) => {
    return fields[fieldName].error;
  };

  const updateCreditCardFromResponse = (response) => {
    const { card } = response.sourceOfFunds.provided;

    updateCreditCard({
      sessionId: response.session.id,
      expiryMonth: card.expiry.month,
      expiryYear: card.expiry.year,
      cardType: card.brand,
      cardNumber: card.number,
      securityCode: card.securityCode,
      apiVersion: MASTERCARD_VERSION,
      merchantId: MASTERCARD_MERCHANT_ID,
    });
  };

  const setFieldValue = (field) => {
    if (field.value) {
      dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_ERROR, payload: { name: field.name, error: null } });
    } else {
      dispatchFieldAction({
        type: FIELD_ACTIONS.SET_FIELD_ERROR,
        payload: { name: field.name, error: { message: resolveErrorMessage(field.name, REQUIRED_FIELD_ERROR_KEY) } },
      });
    }
    dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_VALUE, payload: field });
    updateCreditCard({ [field.name]: field.value });
  };

  const validateForm = () => {
    ['cardNumber', 'nameOnCard', 'securityCode', 'expiryMonth', 'expiryYear'].forEach((fieldName) => {
      if (!fields[fieldName].touched) {
        dispatchFieldAction({ type: FIELD_ACTIONS.SET_FIELD_TOUCHED, payload: { name: fieldName } });
        dispatchFieldAction({
          type: FIELD_ACTIONS.SET_FIELD_ERROR,
          payload: { name: fieldName, error: { message: resolveErrorMessage(fieldName, REQUIRED_FIELD_ERROR_KEY) } },
        });
      }
    });
  };

  return {
    setFieldValue,
    getFieldError,
    validateForm,
    isLoading,
    hasLoadingError,
    formKey,
    isUpdating: isUpdatingMastercardSession,
    reload: loadAndInitializeMastercard,
  };
};

export default useMastercardSession;
